import { useMemo } from 'react';

import { useReducedMotion } from '../../../shared/hooks/useReducedMotion';
import { generatePortrait } from '../../../shared/placeholder/portrait';
import type { PublicSettings } from '../types';
import { Reveal } from './Reveal';

export function About({ settings }: { readonly settings: PublicSettings }) {
  const reducedMotion = useReducedMotion();

  const portraitSrc = useMemo(
    () => settings.portrait?.url ?? generatePortrait({ reducedMotion }),
    [settings.portrait, reducedMotion],
  );

  const isGenerated = settings.portrait === null;

  return (
    <>
      <Reveal>
        {/*
          The API renders this from markdown with Markdig and sanitises it with
          HtmlSanitizer before it leaves the server — a narrow tag allowlist,
          http/https/mailto schemes only, and rel="noopener" forced on every anchor.
          That server-side guarantee is what makes this safe; never point it at a string
          the client assembled.
        */}
        <div
          className="prose-portfolio [&_a]:text-accent [&_a]:underline [&_p]:mb-4 [&_strong]:font-medium [&_strong]:text-text"
          dangerouslySetInnerHTML={{ __html: settings.aboutHtml }}
        />
      </Reveal>

      <Reveal className="mt-8 flex flex-wrap items-end gap-[22px]">
        <figure className="relative isolate m-0 w-full max-w-[240px] flex-none motion-safe:animate-float sm:w-[210px]">
          <span
            aria-hidden="true"
            className="absolute inset-0 -z-10 translate-x-3 translate-y-3 rounded-md border border-accent-line"
          />
          <div className="relative overflow-hidden rounded-md border border-line bg-well">
            <img
              src={portraitSrc}
              alt={isGenerated ? '' : `Portrait of ${settings.displayName}`}
              width={settings.portrait?.width ?? 800}
              height={settings.portrait?.height ?? 1000}
              className="block aspect-[4/5] size-full object-cover"
              loading="lazy"
            />
          </div>
        </figure>

        <figcaption className="min-w-[170px] flex-1 pb-1.5 text-[13px] leading-[1.75] text-faint">
          <span className="mb-1.5 block text-[10px] font-semibold uppercase tracking-label text-accent">
            {isGenerated ? 'Placeholder portrait' : settings.displayName}
          </span>
          Lagos, Nigeria · GMT+1
        </figcaption>
      </Reveal>
    </>
  );
}
